import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [locale, setLocale] = useState(localStorage.getItem('locale') || 'de');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(localStorage.getItem('viewMode') || 'user'); // 'admin' or 'user'

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setLocale(response.data.locale);
      
      // Set viewMode for admin users
      if (response.data.is_admin) {
        const savedViewMode = localStorage.getItem('viewMode');
        // For admins: respect saved preference, but default to 'admin' for new sessions
        if (savedViewMode === 'admin' || savedViewMode === 'user') {
          setViewMode(savedViewMode);
        } else {
          // No valid saved viewMode - default to admin
          setViewMode('admin');
          localStorage.setItem('viewMode', 'admin');
        }
        console.log('👤 Admin user detected, viewMode:', savedViewMode || 'admin');
      } else {
        // Non-admin users always see user view
        setViewMode('user');
        localStorage.setItem('viewMode', 'user');
      }
      
      // Fetch subscription with features
      await fetchSubscription();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/current`);
      
      // Check if admin is simulating a plan
      const simulatedFeatures = localStorage.getItem('simulatedFeatures');
      const currentViewMode = localStorage.getItem('viewMode');
      
      // Get user from response if not in state yet (for initial load)
      const currentUser = user || (await axios.get(`${API}/auth/me`).then(r => r.data).catch(() => null));
      
      if (currentUser?.is_admin && currentViewMode === 'user' && simulatedFeatures) {
        // Override features with simulated ones
        try {
          response.data.features = JSON.parse(simulatedFeatures);
          console.log('🔄 Simulated features applied:', response.data.features);
        } catch (e) {
          console.error('Failed to parse simulated features:', e);
        }
      }
      
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
    }
  };

  const hasFeature = (featureName) => {
    if (!subscription) return false;
    return subscription.features?.[featureName] === true;
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      setLocale(userData.locale);
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('locale', userData.locale);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const signup = async (email, password, selectedLocale) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email,
        password,
        locale: selectedLocale
      });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      setLocale(userData.locale);
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('locale', userData.locale);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Signup failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setSubscription(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const switchLocale = (newLocale) => {
    setLocale(newLocale);
    localStorage.setItem('locale', newLocale);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      subscription, 
      token, 
      locale, 
      loading, 
      viewMode, 
      setViewMode, 
      login, 
      signup, 
      logout, 
      switchLocale,
      hasFeature,
      refreshSubscription: fetchSubscription
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
